import * as React from "react";
import { useCallback, useRef, useState } from "react";
import { makeStyles, mergeClasses, tokens, Button } from "@fluentui/react-components";
import { ChevronDown20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

export function useScrollFade<T extends HTMLElement = HTMLDivElement>() {
  const scrollRef = useRef<T>(null);
  const [showFade, setShowFade] = useState(false);

  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    if (el) setShowFade(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  }, []);

  const scrollDown = useCallback(() => {
    scrollRef.current?.scrollBy({ top: 240, behavior: "smooth" });
  }, []);

  return { scrollRef, showFade, updateFade, scrollDown };
}

const useStyles = makeStyles({
  fade: {
    position: "sticky",
    bottom: 0,
    height: "40px",
    marginTop: "-40px",
    pointerEvents: "none",
    opacity: 0,
    transition: "opacity 0.15s ease",
    background: `linear-gradient(to bottom, transparent, ${tokens.colorNeutralBackground1})`,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-end",
  },
  visible: { opacity: 1 },
  chevron: {
    pointerEvents: "auto",
    minWidth: "28px",
    height: "28px",
    padding: 0,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
});

interface ScrollFadeProps {
  visible: boolean;
  onClick: () => void;
}

export const ScrollFade: React.FC<ScrollFadeProps> = ({ visible, onClick }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  return (
    <div className={mergeClasses(styles.fade, visible && styles.visible)}>
      {visible && (
        <Button
          className={styles.chevron}
          size="small"
          appearance="subtle"
          icon={<ChevronDown20Regular />}
          aria-label={t("common.scrollDown")}
          onClick={onClick}
        />
      )}
    </div>
  );
};
